import { ArrowRight, X } from 'lucide-preact';
import type { MapPropertyTooltipData } from './WorldMap';

interface MapPropertyTooltipProps {
  property: MapPropertyTooltipData;
  style?: Record<string, string>;
  pinned?: boolean;
  onClose?: () => void;
  onViewDetails?: (property: MapPropertyTooltipData) => void;
}

export function MapPropertyTooltip({
  property,
  style,
  pinned = false,
  onClose,
  onViewDetails,
}: MapPropertyTooltipProps) {
  const metaParts = [property.country, property.currency].filter(Boolean);

  return (
    <div
      className={`map-property-tooltip${pinned ? ' map-property-tooltip--pinned' : ' map-property-tooltip--hover'}`}
      style={style}
      role="tooltip"
    >
      {pinned && (
        <button
          type="button"
          className="map-property-tooltip__close"
          aria-label="Close property preview"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onClose?.();
          }}
        >
          <X size={14} strokeWidth={2} aria-hidden="true" />
        </button>
      )}

      <div className="map-property-tooltip__header">
        <div className="map-property-tooltip__image-wrap">
          <img
            src={property.imageUrl}
            alt={property.title}
            className="map-property-tooltip__image"
            onError={(event) => {
              (event.target as HTMLImageElement).src =
                property.fallbackImageUrl || '/images/bg-wide2.jpg';
            }}
          />
        </div>

        <div className="map-property-tooltip__summary">
          {property.unitType && (
            <span className="map-property-tooltip__tag">{property.unitType}</span>
          )}
          <h3 className="map-property-tooltip__title">{property.title}</h3>
          {metaParts.length > 0 && (
            <p className="map-property-tooltip__meta">{metaParts.join(' • ')}</p>
          )}
          {property.operatingModel && (
            <p className="map-property-tooltip__operating-model">{property.operatingModel}</p>
          )}
        </div>
      </div>

      {property.description && (
        <p className="map-property-tooltip__description">{property.description}</p>
      )}

      {!pinned && (
        <p className="map-property-tooltip__hint">Click pin for details</p>
      )}

      {pinned && (
        <button
          type="button"
          className="map-property-tooltip__action"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onViewDetails?.(property);
          }}
        >
          <span>View property details</span>
          <ArrowRight size={14} strokeWidth={2} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
